import React, { useState, useEffect } from 'react';
import { MapPin, Search, Navigation, Check, X, Map as MapIcon, AlertCircle } from 'lucide-react';
import apiService from '../../apiService';
import MapPickerModal from '../MapPickerModal';
import { isValidPincode, formatEta, describeShipping, rememberPincode } from '../../utils/deliveryHelpers';

const LocationModal = ({
  isOpen,
  onClose,
  locations = [],
  selectedLocation,
  setSelectedLocation,
  appTheme = 'blue',
}) => {
  const [query, setQuery] = useState('');
  const [detecting, setDetecting] = useState(false);
  // null while loading; [] when the store has no zones (or the call failed),
  // in which case the static city list is shown instead.
  const [zones, setZones] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  // Outcome of the last map pick: { status: 'checking' | 'ok' | 'unserviceable' | 'error', ... }
  const [mapResult, setMapResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    apiService.getDeliveryZones().then((response) => {
      if (cancelled) return;
      const list = response.success && Array.isArray(response.data?.zones) ? response.data.zones : [];
      setZones(list);
    });

    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasZones = Array.isArray(zones) && zones.length > 0;
  const term = query.trim().toLowerCase();

  const filtered = term
    ? locations.filter(l => l.toLowerCase().includes(term))
    : locations;

  // A zone matches on its name or on any pincode starting with the query.
  const filteredZones = !hasZones
    ? []
    : term
      ? zones.filter(z =>
          z.name.toLowerCase().includes(term)
          || (z.pincodes || []).some(p => String(p).startsWith(term)))
      : zones;

  const handleSelect = (location) => {
    setSelectedLocation(location);
    onClose();
  };

  const openMap = (center = null) => {
    setMapCenter(center);
    setMapOpen(true);
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      openMap();
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDetecting(false);
        openMap({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        // Permission denied or timed out: the shopper can still place the pin by hand.
        setDetecting(false);
        openMap();
      },
      { timeout: 8000 }
    );
  };

  const handleMapConfirm = async (picked) => {
    setMapOpen(false);

    const pincode = String(picked?.pincode || '').trim();
    if (!isValidPincode(pincode)) {
      setMapResult({ status: 'error', message: 'Could not find a pincode for that spot. Move the pin to a nearby street and try again.' });
      return;
    }

    setMapResult({ status: 'checking', pincode });
    const response = await apiService.checkDeliveryPincode(pincode);
    const delivery = response.success ? response.data?.delivery : null;

    if (!delivery) {
      setMapResult({ status: 'error', message: 'Could not check delivery for this location. Please try again.' });
      return;
    }

    if (delivery.serviceable === false) {
      setMapResult({ status: 'unserviceable', pincode, address: picked.address });
      return;
    }

    rememberPincode(pincode);
    const area = delivery.zone_name || picked.city || 'Pincode';
    setSelectedLocation(`${area}, ${pincode}`);
    setMapResult({ status: 'ok', pincode, address: picked.address, delivery });
  };

  const zoneTerms = (zone) => [formatEta(zone), describeShipping(zone)].filter(Boolean).join(' · ');

  // Split "City, State" for richer display
  const parseLocation = (loc) => {
    const parts = loc.split(',');
    return { city: parts[0]?.trim() || loc, state: parts[1]?.trim() || '' };
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="relative w-full max-w-md bg-white rounded-t-3xl overflow-hidden shadow-2xl"
        style={{ maxHeight: '90vh' }}>

        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1.5 bg-gray-200 rounded-full" />
        </div>

        {/* Hero header */}
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 px-6 pt-4 pb-8 overflow-hidden">
          {/* decorative circles */}
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full" />
          <div className="absolute -bottom-8 -left-4 w-24 h-24 bg-white/10 rounded-full" />

          <div className="flex items-start justify-between relative z-10">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Choose Location</h2>
              <p className="text-blue-100 text-sm">Select your delivery area</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors text-white"
            >
              <X size={18} />
            </button>
          </div>

          {/* Current selection pill */}
          {selectedLocation && (
            <div className="mt-4 flex items-center gap-2 bg-white/20 rounded-xl px-4 py-2.5 w-fit relative z-10">
              <MapPin size={16} className="text-white flex-shrink-0" />
              <span className="text-white text-sm font-medium">{selectedLocation}</span>
            </div>
          )}

          {/* Large decorative pin */}
          <div className="absolute right-6 bottom-2 opacity-20">
            <MapPin size={80} className="text-white" />
          </div>
        </div>

        <div className="px-5 pt-5 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>

          {/* Search */}
          <div className="relative mb-4">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={hasZones ? 'Search area or pincode...' : 'Search city or area...'}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm outline-none focus:border-blue-400 focus:bg-white transition-all"
            />
          </div>

          {/* Detect location button */}
          <button
            onClick={handleDetectLocation}
            disabled={detecting}
            className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-all mb-3 group"
          >
            <div className={`relative flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md ${detecting ? 'animate-pulse' : ''}`}>
              <Navigation size={18} className="text-white" />
              {detecting && (
                <span className="absolute inset-0 rounded-xl bg-blue-400 animate-ping opacity-40" />
              )}
            </div>
            <div className="text-left">
              <p className="font-semibold text-blue-700 text-sm">
                {detecting ? 'Detecting…' : 'Use my current location'}
              </p>
              <p className="text-blue-500 text-xs">Auto-detect via GPS</p>
            </div>
          </button>

          {/* Pick on map */}
          <button
            onClick={() => openMap()}
            className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-gray-100 bg-white hover:border-blue-200 hover:bg-gray-50 transition-all mb-4"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
              <MapIcon size={18} className="text-gray-600" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-800 text-sm">Choose on map</p>
              <p className="text-gray-400 text-xs">Drop a pin to check delivery to your address</p>
            </div>
          </button>

          {/* Result of the last map pick */}
          {mapResult && (
            <div
              className={`flex items-start gap-3 p-4 rounded-2xl mb-5 ${
                mapResult.status === 'ok'
                  ? 'bg-green-50 border border-green-200'
                  : mapResult.status === 'checking'
                    ? 'bg-gray-50 border border-gray-100'
                    : 'bg-red-50 border border-red-200'
              }`}
              role="status"
            >
              {mapResult.status === 'ok' ? (
                <Check size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
              ) : mapResult.status === 'checking' ? (
                <MapPin size={18} className="text-gray-400 flex-shrink-0 mt-0.5 animate-pulse" />
              ) : (
                <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                {mapResult.status === 'checking' && (
                  <p className="text-sm text-gray-600">Checking delivery to {mapResult.pincode}…</p>
                )}
                {mapResult.status === 'ok' && (
                  <>
                    <p className="text-sm font-semibold text-green-700">
                      We deliver to {mapResult.pincode}
                      {mapResult.delivery.zone_name ? ` (${mapResult.delivery.zone_name})` : ''}
                    </p>
                    {zoneTerms(mapResult.delivery) && (
                      <p className="text-xs text-green-700 mt-0.5">{zoneTerms(mapResult.delivery)}</p>
                    )}
                    {mapResult.address && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{mapResult.address}</p>
                    )}
                  </>
                )}
                {mapResult.status === 'unserviceable' && (
                  <>
                    <p className="text-sm font-semibold text-red-600">
                      Sorry, we don't deliver to {mapResult.pincode} yet
                    </p>
                    {mapResult.address && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{mapResult.address}</p>
                    )}
                  </>
                )}
                {mapResult.status === 'error' && (
                  <p className="text-sm text-red-600">{mapResult.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-xs text-gray-400 font-medium">
              {hasZones ? 'AREAS WE DELIVER TO' : 'OR CHOOSE AREA'}
            </span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* Location list */}
          <div className="space-y-2">
            {zones === null ? (
              <p className="text-center text-gray-400 text-sm py-6">Loading delivery areas…</p>
            ) : hasZones ? (
              filteredZones.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">
                  No delivery area matches "{query.trim()}"
                </p>
              ) : (
                filteredZones.map((zone) => {
                  const isSelected = selectedLocation === zone.name;
                  const terms = zoneTerms(zone);
                  const pincodeCount = (zone.pincodes || []).length;
                  return (
                    <button
                      key={zone.id}
                      onClick={() => handleSelect(zone.name)}
                      className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
                        isSelected
                          ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm'
                          : 'border-gray-100 bg-white hover:border-blue-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                        isSelected
                          ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                          : 'bg-gray-100'
                      }`}>
                        <MapPin size={20} className={isSelected ? 'text-white' : 'text-gray-500'} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                          {zone.name}
                        </p>
                        {terms && <p className="text-xs text-gray-500 mt-0.5 truncate">{terms}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">
                          {pincodeCount} pincode{pincodeCount === 1 ? '' : 's'}
                          {zone.cod_enabled ? ' · Pay on delivery' : ''}
                        </p>
                      </div>

                      {isSelected && (
                        <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Check size={14} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })
              )
            ) : filtered.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">No locations found</p>
            ) : (
              filtered.map((location, index) => {
                const { city, state } = parseLocation(location);
                const isSelected = selectedLocation === location;
                return (
                  <button
                    key={index}
                    onClick={() => handleSelect(location)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm'
                        : 'border-gray-100 bg-white hover:border-blue-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                      isSelected
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                        : 'bg-gray-100'
                    }`}>
                      <MapPin size={20} className={isSelected ? 'text-white' : 'text-gray-500'} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                        {city}
                      </p>
                      {state && (
                        <p className="text-xs text-gray-400 mt-0.5">{state}</p>
                      )}
                    </div>

                    {isSelected && (
                      <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-md">
                        <Check size={14} className="text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {mapOpen && (
        <MapPickerModal
          onClose={() => setMapOpen(false)}
          onConfirm={handleMapConfirm}
          initialLat={mapCenter?.lat}
          initialLng={mapCenter?.lng}
        />
      )}
    </div>
  );
};

const locations = [
  'Gurugram, Haryana',
  'New Delhi, Delhi',
  'Noida, Uttar Pradesh',
  'Faridabad, Haryana',
  'Ghaziabad, Uttar Pradesh',
];

export default LocationModal;
